// src/pages/CaseDetail/components/PrescriptionForm.jsx
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import Select from '../../../components/ui/Select';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import DentalChartIso from '../../case-dentist-assistant/DentalChartIso';

const PrescriptionForm = ({ caseData, onDataChange, allowPrescriptionEdit }) => {
  const [formData, setFormData] = useState({
    archToTreat: '',
    treatmentType: '',
    overjet: '',
    overbite: '',
    midline: '',
    posteriorCrossbite: '',
    spacing: '',
    sequentialDistalization: '',
    expand: { upper: '', lower: '' },
    procline: { upper: '', lower: '' },
    canine: { upper: '', lower: '' },
    molar: { upper: '', lower: '' },
    iprAnterior: { upper: '', lower: '' },
    iprPosteriorRight: { upper: '', lower: '' },
    iprPosteriorLeft: { upper: '', lower: '' },
    toothMovementRestrictions: [],
    attachmentRestrictions: [],
    extractions: [],
    missingTeeth: [],
    questionsNotes: ''
  });

useEffect(() => {
  if (caseData) {
    const rawArch = caseData.archToTreat || caseData.arch_to_treat || '';
    const archNorm = rawArch ? String(rawArch).toLowerCase() : '';
    const archValue = ['upper', 'lower', 'both'].includes(archNorm) ? archNorm : '';
    setFormData({
      // Valeurs de la table cases
      archToTreat: archValue,
      treatmentType: caseData.treatmentType || caseData.treatment_type || '',
      questionsNotes: caseData.questionsNotes || caseData.questions_notes || '',
      
      // Valeurs de case_treatment_specs (si elles existent)
      overjet: caseData.treatmentSpecs?.overject || '',
      overbite: caseData.treatmentSpecs?.overbite || '',
      midline: caseData.treatmentSpecs?.midline || '',
      posteriorCrossbite: caseData.treatmentSpecs?.posterior_crossbite || '',
      spacing: caseData.treatmentSpecs?.spacing || '',
      sequentialDistalization: caseData.treatmentSpecs?.sequential_distalization || '',
      
      // JSONB fields
      expand: caseData.treatmentSpecs?.expand || { upper: '', lower: '' },
      procline: caseData.treatmentSpecs?.procline || { upper: '', lower: '' },
      canine: caseData.treatmentSpecs?.canine || { upper: '', lower: '' },
      molar: caseData.treatmentSpecs?.molar || { upper: '', lower: '' },
      iprAnterior: caseData.treatmentSpecs?.ipr_anterior || { upper: '', lower: '' },
      iprPosteriorRight: caseData.treatmentSpecs?.ipr_posterior_right || { upper: '', lower: '' },
      iprPosteriorLeft: caseData.treatmentSpecs?.ipr_posterior_left || { upper: '', lower: '' },
      
      // Arrays
      toothMovementRestrictions: caseData.treatmentSpecs?.tooth_mvt_restrictions || [],
      attachmentRestrictions: caseData.treatmentSpecs?.attachment_restrictions || [],
      extractions: caseData.treatmentSpecs?.extractions || [],
      missingTeeth: caseData.treatmentSpecs?.missing_teeth || []
    });
  }
}, [caseData]);
  const handleInputChange = (field, value) => {
    const newData = { ...formData, [field]: value };
    setFormData(newData);
    if (onDataChange) onDataChange(newData);
  };

  const toggleArrayValue = (field, value) => {
    const currentArray = formData[field];
    const newArray = currentArray.includes(value)
      ? currentArray.filter((item) => item !== value)
      : [...currentArray, value];
    handleInputChange(field, newArray);
  };

  /** Aligné sur `cases.arch_to_treat` (lower case) et handleSaveDetails. */
  const archOptions = [
    { value: 'upper', label: 'Arcade Supérieure' },
    { value: 'lower', label: 'Arcade Inférieure' },
    { value: 'both', label: 'Les Deux Arcades' },
  ];

  const treatmentTypeOptions = [
    { value: 'Full Arch', label: 'Arcade Complète' },
    { value: 'Partial', label: 'Traitement Partiel' },
    { value: 'Comprehensive', label: 'Traitement Complet' },
    { value: 'Anterior', label: 'Antérieur Uniquement' }
  ];

  const improveOptions = [
    { value: 'Improve', label: 'Améliorer' },
    { value: 'Maintain', label: 'Maintenir' },
    { value: 'Correct', label: 'Corriger' }
  ];

  const spacingOptions = [
    { value: 'Close All', label: 'Fermer Tout' },
    { value: 'Maintain', label: 'Maintenir' },
    { value: 'Create', label: 'Créer' }
  ];

  const yesNoOptions = [
    { value: 'Yes', label: 'Oui' },
    { value: 'No', label: 'Non' }
  ];

  const iprOptions = [
    { value: 'Primarily', label: 'Principalement' },
    { value: 'As Needed', label: 'Si Nécessaire' },
    { value: 'None', label: 'Aucun' }
  ];

  const expandOptions = [
    { value: 'Primarily', label: 'Principalement' },
    { value: 'As Needed', label: 'Si Nécessaire' },
    { value: 'None', label: 'Aucun' }
  ];

  const archReadLabel =
    archOptions.find(
      (o) => o.value === String(formData.archToTreat || '').toLowerCase()
    )?.label ||
    formData.archToTreat ||
    'Non défini';

  if (!allowPrescriptionEdit) {
    return (
      <div className="space-y-6 mt-6">
        <h3 className="text-base font-semibold text-foreground">Spécifications du Traitement</h3>
        
        {/* Affichage en lecture seule */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-3 rounded-lg case-detail-section-surface">
            <p className="text-xs text-muted-foreground">Arcade à Traiter</p>
            <p className="text-sm font-medium text-foreground">{archReadLabel}</p>
          </div>
         
          <div className="p-3 rounded-lg case-detail-section-surface">
            <p className="text-xs text-muted-foreground">Overjet</p>
            <p className="text-sm font-medium text-foreground">{formData.overjet || 'Non défini'}</p>
          </div>
          <div className="p-3 rounded-lg case-detail-section-surface">
            <p className="text-xs text-muted-foreground">Overbite</p>
            <p className="text-sm font-medium text-foreground">{formData.overbite || 'Non défini'}</p>
          </div>
          <div className="p-3 rounded-lg case-detail-section-surface">
            <p className="text-xs text-muted-foreground">Ligne Médiane</p>
            <p className="text-sm font-medium text-foreground">{formData.midline || 'Non défini'}</p>
          </div>
          <div className="p-3 rounded-lg case-detail-section-surface">
            <p className="text-xs text-muted-foreground">Espacement</p>
            <p className="text-sm font-medium text-foreground">{formData.spacing || 'Non défini'}</p>
          </div>
        </div>

        {/* Afficher les notes si présentes */}
        {formData.questionsNotes && (
          <div className="p-4 rounded-lg case-detail-section-surface mt-4">
            <p className="text-xs text-muted-foreground mb-1">Notes</p>
            <p className="text-sm text-foreground whitespace-pre-wrap">{formData.questionsNotes}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6 mt-6">
      {/* Prescription d'Alignement */}
      <Card>
        <CardHeader>
          <CardTitle>Prescription d'Alignement</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Arcade à Traiter</Label>
            <Select
              options={archOptions}
              value={formData.archToTreat || ''}
              onChange={(value) => handleInputChange('archToTreat', value)}
              placeholder="Choisir une arcade"
            />
          </div>

  

          <div className="space-y-2">
            <Label>Overjet</Label>
            <Select
              options={improveOptions}
              value={formData.overjet}
              onChange={(value) => handleInputChange('overjet', value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Overbite</Label>
            <Select
              options={improveOptions}
              value={formData.overbite}
              onChange={(value) => handleInputChange('overbite', value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Ligne Médiane</Label>
            <Select
              options={improveOptions}
              value={formData.midline}
              onChange={(value) => handleInputChange('midline', value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Occlusion Croisée Postérieure</Label>
            <Select
              options={improveOptions}
              value={formData.posteriorCrossbite}
              onChange={(value) => handleInputChange('posteriorCrossbite', value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Espacement</Label>
            <Select
              options={spacingOptions}
              value={formData.spacing}
              onChange={(value) => handleInputChange('spacing', value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Distalisation Séquentielle</Label>
            <Select
              options={yesNoOptions}
              value={formData.sequentialDistalization}
              onChange={(value) => handleInputChange('sequentialDistalization', value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Encombrement & Espacement */}
      <Card>
        <CardHeader>
          <CardTitle>Encombrement & Espacement</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <Label className="text-base">Expansion</Label>
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Arcade Supérieure</Label>
              <Select
                options={expandOptions}
                value={formData.expand.upper}
                onChange={(value) => handleInputChange('expand', { ...formData.expand, upper: value })}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Arcade Inférieure</Label>
              <Select
                options={expandOptions}
                value={formData.expand.lower}
                onChange={(value) => handleInputChange('expand', { ...formData.expand, lower: value })}
              />
            </div>
          </div>

          <div className="space-y-3">
            <Label className="text-base">Proclination</Label>
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Arcade Supérieure</Label>
              <Select
                options={expandOptions}
                value={formData.procline.upper}
                onChange={(value) => handleInputChange('procline', { ...formData.procline, upper: value })}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Arcade Inférieure</Label>
              <Select
                options={expandOptions}
                value={formData.procline.lower}
                onChange={(value) => handleInputChange('procline', { ...formData.procline, lower: value })}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Relation Antéro-Postérieure */}
      <Card>
        <CardHeader>
          <CardTitle>Relation Antéro-Postérieure</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <Label className="text-base">Canine</Label>
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Arcade Supérieure</Label>
              <Select
                options={improveOptions}
                value={formData.canine.upper}
                onChange={(value) => handleInputChange('canine', { ...formData.canine, upper: value })}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Arcade Inférieure</Label>
              <Select
                options={improveOptions}
                value={formData.canine.lower}
                onChange={(value) => handleInputChange('canine', { ...formData.canine, lower: value })}
              />
            </div>
          </div>

          <div className="space-y-3">
            <Label className="text-base">Molaire</Label>
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Arcade Supérieure</Label>
              <Select
                options={improveOptions}
                value={formData.molar.upper}
                onChange={(value) => handleInputChange('molar', { ...formData.molar, upper: value })}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Arcade Inférieure</Label>
              <Select
                options={improveOptions}
                value={formData.molar.lower}
                onChange={(value) => handleInputChange('molar', { ...formData.molar, lower: value })}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* IPR */}
      <Card>
        <CardHeader>
          <CardTitle>Réduction Inter-Proximale (IPR)</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="space-y-3">
            <Label className="text-base">IPR Antérieur</Label>
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Supérieur</Label>
              <Select
                options={iprOptions}
                value={formData.iprAnterior.upper}
                onChange={(value) => handleInputChange('iprAnterior', { ...formData.iprAnterior, upper: value })}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Inférieur</Label>
              <Select
                options={iprOptions}
                value={formData.iprAnterior.lower}
                onChange={(value) => handleInputChange('iprAnterior', { ...formData.iprAnterior, lower: value })}
              />
            </div>
          </div>

          <div className="space-y-3">
            <Label className="text-base">IPR Postérieur Droit</Label>
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Supérieur</Label>
              <Select
                options={iprOptions}
                value={formData.iprPosteriorRight.upper}
                onChange={(value) => handleInputChange('iprPosteriorRight', { ...formData.iprPosteriorRight, upper: value })}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Inférieur</Label>
              <Select
                options={iprOptions}
                value={formData.iprPosteriorRight.lower}
                onChange={(value) => handleInputChange('iprPosteriorRight', { ...formData.iprPosteriorRight, lower: value })}
              />
            </div>
          </div>

          <div className="space-y-3">
            <Label className="text-base">IPR Postérieur Gauche</Label>
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Supérieur</Label>
              <Select
                options={iprOptions}
                value={formData.iprPosteriorLeft.upper}
                onChange={(value) => handleInputChange('iprPosteriorLeft', { ...formData.iprPosteriorLeft, upper: value })}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Inférieur</Label>
              <Select
                options={iprOptions}
                value={formData.iprPosteriorLeft.lower}
                onChange={(value) => handleInputChange('iprPosteriorLeft', { ...formData.iprPosteriorLeft, lower: value })}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Restrictions et Sélections Dentaires — schéma ISO (FDI), stockage universal 1–32 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Restrictions de Mouvement Dentaire</CardTitle>
          </CardHeader>
          <CardContent>
            <DentalChartIso
              idPrefix="tooth-mvt"
              selectedUniversal={formData.toothMovementRestrictions}
              onToggle={(u) => toggleArrayValue('toothMovementRestrictions', u)}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Restrictions d'Attachements</CardTitle>
          </CardHeader>
          <CardContent>
            <DentalChartIso
              idPrefix="attach"
              selectedUniversal={formData.attachmentRestrictions}
              onToggle={(u) => toggleArrayValue('attachmentRestrictions', u)}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Extractions</CardTitle>
          </CardHeader>
          <CardContent>
            <DentalChartIso
              idPrefix="extract"
              selectedUniversal={formData.extractions}
              onToggle={(u) => toggleArrayValue('extractions', u)}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Dents Manquantes</CardTitle>
          </CardHeader>
          <CardContent>
            <DentalChartIso
              idPrefix="missing"
              selectedUniversal={formData.missingTeeth}
              onToggle={(u) => toggleArrayValue('missingTeeth', u)}
            />
          </CardContent>
        </Card>
      </div>

      {/* Notes */}
      <Card>
        <CardHeader>
          <CardTitle>Notes Supplémentaires</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            placeholder="Ajoutez des notes supplémentaires pour ce traitement..."
            value={formData.questionsNotes}
            onChange={(e) => handleInputChange('questionsNotes', e.target.value)}
            rows={4}
            className="w-full"
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default PrescriptionForm;